import React, { useState, useEffect } from 'react';
import { formatPhone, isValidPhone } from '../../utils/phone';
import { Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useDoorlyProAuth } from '../../context/DoorlyProAuthContext';
import { US_STATES } from '../../constants/usStates';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ⚠️ MOVED TO utils/phone.js. This exact function existed here and in
// RequestShowingModal, and a third reader — the admin partner panel — needed a
// display variant of it. Three copies of "what a phone number looks like" is
// three places to fix the day the format changes.

const inputStyle = {
  width: '100%',
  height: 48,
  padding: '0 14px',
  borderRadius: 10,
  border: '1px solid #e5e7eb',
  fontSize: 14,
  color: '#111827',
  backgroundColor: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
};

const focusStyle = (e) => (e.target.style.borderColor = '#3a6ad9');
const blurStyle = (e) => (e.target.style.borderColor = '#e5e7eb');

export default function DoorlyProRegister() {
  const navigate = useNavigate();
  const location = useLocation();
  const { partnerLogin } = useDoorlyProAuth();

  // ── the answers from /doorlypro/start ─────────────────────────────────────
  //
  // ⚠️ THREE SOURCES, READ IN ONE ORDER, AND A NEUTRAL FALLBACK. Router state is
  // the cleanest but does not survive a refresh; sessionStorage does; the query
  // string survives a copied link. If all three are missing — a partner who typed
  // the URL, or arrived on an INVITE link, which deliberately comes straight here
  // — the form still renders and the server still validates. That is the whole
  // reason this is not treated as a gate: it is allowed to be absent.
  // 🔑 THE PATH IS THE DOOR. /doorlypro/start sends an individual to
  // /doorlypro/register and a firm to /doorlypro/register-account, so which
  // registration this is no longer rides on a query parameter that the form has
  // to interpret. Same component behind both — two forms sharing a name, email,
  // password, company and license would be the mirror-drift hazard, and the parts
  // that differ are a heading and one notice.
  const isAccountRegistration = location.pathname.endsWith('/register-account');

  const start = (() => {
    const fromState = location.state && location.state.doorlyproStart;
    if (fromState) return fromState;
    try {
      const raw = sessionStorage.getItem('doorlypro_start');
      if (raw) return JSON.parse(raw);
    } catch (_) { /* private mode, or a value we did not write */ }
    const q = new URLSearchParams(location.search);
    if (q.get('type')) {
      return { partner_type: q.get('type'), role: q.get('role') || '', enterprise: q.get('enterprise') === '1' };
    }
    return null;
  })();



  // ⚠️ FETCHED, NOT MIRRORED. The mapping from (type, role) to WHICH license
  // lives once, in backend/constants/partnerLicense.js, and is served by
  // GET /api/doorlypro/license-ask. A second copy here would be free to disagree
  // with the column the server files the number in, which is the exact class of
  // bug the registry split and the partnerStatuses parity test both exist for.
  const [ask, setAsk] = useState(null);
  // 🚨 SETTLED, NOT LOADED — AND THAT IS THE FLASH FIX. The license block renders
  // on `ask`, which starts null and is filled by a fetch, so every load of this
  // page drew the form WITHOUT the license row and then grew by a row when the
  // answer arrived. The form visibly rearranged itself under the reader's cursor.
  //
  // ⚠️ THE FLAG IS "THE QUESTION HAS BEEN ANSWERED", NOT "AN ANSWER EXISTS". A
  // homebuilder's correct answer IS null, so waiting on `ask` itself would wait
  // forever for them. It flips on success AND on failure, because a failed
  // lookup is also a settled state — the fallback label renders and the form is
  // usable; what it must not do is leave the page blank.
  //
  // ⚠️ INITIALISED TRUE WHEN THERE IS NOTHING TO FETCH. The effect below returns
  // early with no answers — an invite link comes straight here — and a flag that
  // only the fetch can clear would hold that reader on the spinner permanently.
  const [askSettled, setAskSettled] = useState(!(start && start.partner_type));
  useEffect(() => {
    if (!start || !start.partner_type) return;
    let live = true;
    axios.get(`${API_URL}/api/doorlypro/license-ask`, {
      params: { type: start.partner_type, role: start.role || '' },
    }).then((r) => { if (live) { setAsk(r.data && r.data.ask); setAskSettled(true); } })
      .catch(() => {
        // Label falls back to the generic one; the field still works.
        if (live) setAskSettled(true);
      });
    return () => { live = false; };
  }, [start && start.partner_type, start && start.role]);   // eslint-disable-line react-hooks/exhaustive-deps

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: '',
    // ⚠️ SEEDED FROM THE QUESTION SET, AND STILL SENT. The <select> is gone from
    // the UI but partner_type is what the server fails closed on, so it must
    // still be in the payload. Defaults to realtor only when the answers are
    // absent entirely, which is what the field defaulted to before.
    partner_type: (start && start.partner_type) || 'realtor',
    // 🔑 THE Q2 ANSWER, SENT AND KEPT. It used to reach this form and go no
    // further, which is why nothing ever granted the enterprise role. The server
    // stores it as a CLAIM and decides what it grants when the firm is created.
    // ⚠️ FROM THE PATH FIRST. The route is what /start chose; the stored answer is
    // the fallback for a reload that kept session state but lost nothing else.
    enterprise: isAccountRegistration || !!(start && start.enterprise),
    company_name: '',
    broker_account_claim: '',
    license_number: '',
    license_state: '',
    // 🔑 EVERY STATE THEY ARE LICENSED IN, primary first. license_state stays as
    // that primary so every existing reader — the Partners table's State column
    // and filter, the admin edit form — keeps working unchanged.
    license_states: [],
    // 🔑 WHERE THEY WORK, WHICH IS NOT WHERE THEIR LICENSE IS. It decides which
    // Partner Success rep they get — Texas or not Texas — and the license state
    // could not answer it: an NMLS id is national, and a homebuilder is asked for
    // no license at all so the field is not even on their screen.
    work_state: '',
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  // 🔑 FETCHED, NOT MIRRORED. The footprint moves when a license lands; a copy in
  // the bundle would keep telling people a state is closed after it opened — the
  // same rule partnerStatuses and license-ask already follow.
  const [footprint, setFootprint] = useState(null);

  // ⚠️ ABOVE THE `if (!start)` REDIRECT, because that is an early return and a
  // hook after it runs conditionally — React refuses it, and the build says so.
  // Every hook in this component has to sit before that guard.
  //
  // ⚠️ KEYED ON THE WORK STATE, NOT THE LICENSE STATE. Which market Doorly can
  // serve them in is about where they DO BUSINESS; a license held in a state they
  // do not work in would show the wrong notice.
  useEffect(() => {
    if (!form.work_state) { setFootprint(null); return; }
    let cancelled = false;
    axios.get(`${API_URL}/api/doorlypro/footprint-notice`, { params: { state: form.work_state } })
      .then((r) => { if (!cancelled) setFootprint(r.data?.notice || null); })
      // ⚠️ A FAILED LOOKUP SHOWS NOTHING RATHER THAN GUESSING. The notice is
      // context; registration does not depend on it.
      .catch(() => { if (!cancelled) setFootprint(null); });
    return () => { cancelled = true; };
  }, [form.work_state]);


  // 🚨 "BROKER OF RECORD" IS A REALTOR FACT, AND THIS NOTICE WAS SHOWING IT TO
  // HOMEBUILDERS. A builder has no brokerage and no broker of record; the
  // enterprise role /doorlypro/start offers them is TEAM LEAD, and telling a
  // sales manager at a builder that registering makes them a broker of record is
  // simply a false statement about their license, on the screen where they are
  // deciding whether they are the right person to be here.
  //
  // ⚠️ THE STRUCTURE IS THE SAME FOR ALL THREE — one person opens the firm's
  // account, counter-signs, and sees the team's buyers — so this varies the NOUNS
  // and not the promise. Forking the paragraph three ways would let the three
  // copies drift the next time the counter-signature rule changes.
  //
  // ⚠️ AND IT FALLS BACK RATHER THAN GUESSING. An invite link reaches this form
  // with no answers at all, and "account owner" is true of every type.
  const accountNotice = (() => {
    switch (start && start.partner_type) {
      case 'realtor':
        return { firm: "your firm's account", role: 'the broker of record', people: 'agents' };
      case 'loan_officer':
        return { firm: "your company's account", role: 'the broker on this account', people: 'originators' };
      case 'homebuilder':
        return { firm: "your builder's account", role: 'the team lead', people: 'sales agents' };
      default:
        return { firm: "your firm's account", role: 'the owner of this account', people: 'people' };
    }
  })();

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 🚨 NO ANSWERS MEANS NO FORM. THE NEUTRAL FALLBACK WAS THE BUG.
  //
  // ⚠️ AND THIS GUARD SITS BELOW EVERY HOOK, NOT ABOVE THEM. Placed before the
  // useState/useEffect calls it read naturally and broke the rules of hooks —
  // React requires the same hooks in the same order on every render, and an early
  // return skips the ones after it. The same mistake was made in Homes.js earlier
  // and caught by the same lint rule; a redirect is worth nothing if the
  // component cannot render at all.
  //
  // This used to render with partner_type defaulted to 'realtor' and a generic
  // "License Number" label when all three transports were empty — which is the
  // INCORRECT FORM, quietly: a loan officer typing their NMLS id into a field the
  // server files as a state real-estate license, and a homebuilder shown a
  // license field for a registry they are not on. Rendering something wrong is
  // worse than sending them one screen back.
  //
  // ⚠️ WHAT LEGITIMATELY ARRIVES HERE WITHOUT ANSWERS, AND WHY NONE OF IT LOSES
  // WORK:
  //   · a saved bookmark or a hand-typed URL — nothing typed yet, nothing lost.
  //   · a REFRESH mid-form — the answers are in the QUERY STRING, so they survive
  //     a reload even when sessionStorage is unavailable (private mode). This
  //     branch is not reached. React state would have been lost to the reload
  //     regardless of what this guard does.
  //   · an OLD INVITE EMAIL pointing at /register?invite=N — already sent, and
  //     they exist. The invite id is PRESERVED through the redirect so /start can
  //     read it and skip the question it answers; dropping the param would ask an
  //     invited partner something staff already recorded.
  //   · the back button after reaching /register — goes to /start, which is where
  //     this sends them anyway.
  //
  // ⚠️ replace: true SO BACK DOES NOT PING-PONG. A pushed redirect puts /register
  // in the history behind /start, and Back would bounce off this guard forever.
  if (!start) {
    const invite = new URLSearchParams(location.search).get('invite');
    return <Navigate to={invite ? `/doorlypro/start?invite=${encodeURIComponent(invite)}` : '/doorlypro/start'} replace />;
  }

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // ⚠️ THE LICENSE IS REQUIRED ONLY WHEN THE PAIR CARRIES ONE. A homebuilder
    // holds neither an MLO nor a real-estate license — registryForType has always
    // returned null for them — so demanding one here would block a whole partner
    // type out of a field that should not be on their screen at all.
    const needsLicense = !start || !!ask;
    if (!form.first_name || !form.last_name || !form.email || !form.password || !form.phone
        || !form.company_name || !form.work_state
        || (needsLicense && (!form.license_number || !form.license_state))) {
      setError('Please fill in all required fields.');
      return;
    }
    // 🚨 A MORTGAGE BROKER HOLDS TWO NUMBERS AND BOTH ARE REQUIRED. The firm's
    // number is what Doorly's agreement and the borrower authorization name; a
    // broker who skipped it would be activated against a firm with no
    // registration on file, which is where every existing broker account sits.
    if (ask && ask.alsoAsk && !String(form[ask.alsoAsk.field] || '').trim()) {
      setError(`Please enter your ${ask.alsoAsk.label}.`);
      return;
    }
    // ⚠️ CHECKED HERE TOO, not only on the server. NMLS registers a company and
    // an individual separately, so equal values mean one field was filled in
    // twice — and saying so before the round trip is the difference between a
    // correction and a rejection.
    if (ask && ask.alsoAsk
      && String(form[ask.alsoAsk.field] || '').replace(/\D/g, '')
         === String(form.license_number || '').replace(/\D/g, '')) {
      setError('Those are the same number. NMLS registers a company and an individual '
        + 'separately, so your own NMLS ID and your company\u2019s are never the same.');
      return;
    }
    if (!isValidPhone(form.phone)) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (form.password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // 🚨 THE ROLE GOES WITH IT. This route never received it, so the write
      // dispatched on partner_type alone — and for a mortgage broker that meant
      // the COMPANY number this form asked for landed in the INDIVIDUAL field
      // every gate reads. Every mortgage broker on the books registered that
      // way. The server consults licenseAsk(type, role) now, and it cannot
      // without this.
      const res = await axios.post(`${API_URL}/api/doorlypro/register`, {
        ...form, role: (start && start.role) || '', phone: form.phone.replace(/\D/g, '') });
      if (res.data.token) {
        partnerLogin(res.data.token);
        // 🚨 STRAIGHT TO THE DASHBOARD. This went to /onboarding, whose first
        // screen asked for the company, license number and license state — all
        // three of which this form has just collected. A new partner's first
        // experience of DoorlyPro was re-typing what they had typed a second
        // earlier, before they had seen anything the product does.
        //
        // 🔑 NOTHING IS SKIPPED, IT IS RE-SEQUENCED. Training, the agreement and
        // the rest are dashboard tasks with training first, so the same work is
        // asked for in the same order — with the account already in front of
        // them instead of behind a form.
        navigate('/doorlypro/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/doorlypro/login" className="inline-block">
            <img
              src={`${process.env.PUBLIC_URL}/assets/DoorlyPro.png`}
              alt="DoorlyPro"
              style={{ height: 40, width: 'auto' }}
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'inline'; }}
            />
            <span style={{ display: 'none', fontSize: 24, fontWeight: 800, color: '#3a6ad9' }}>Doorly<span style={{ color: '#111827' }}>Pro</span></span>
          </Link>
          <h1 className="text-2xl font-bold text-black mt-6 mb-1">{isAccountRegistration ? 'Account Registration' : 'Partner Registration'}</h1>
          <p className="text-gray-500 text-sm">Create your DoorlyPro partner account.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          {/* 🚨 THE FORM IS NOT DRAWN UNTIL IT KNOWS ITS OWN SHAPE. Which fields
              this form has depends on an answer that arrives over the network —
              whether the reader's (type, role) pair carries a license at all —
              and rendering first and reflowing second is what made every load of
              this page flash and jump. One placeholder of roughly the form's
              height holds the space so the card does not resize either.

              ⚠️ NOT A SPINNER OVER A HIDDEN FORM. The inputs are unmounted, not
              covered: a field that exists but is invisible can still take focus
              and a keystroke typed into it is lost when the layout settles. */}
          {!askSettled ? (
            <div aria-busy="true" aria-live="polite" style={{ minHeight: 460, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <span className="sr-only">Loading the registration form…</span>
              {[...Array(7)].map((_, i) => (
                <div key={i} style={{ height: i === 0 ? 20 : 48, width: i === 0 ? '40%' : '100%',
                  borderRadius: 10, background: '#f1f3f7' }} />
              ))}
            </div>
          ) : (
          <>
          {error && (
            <div className="mb-5 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">First Name *</label>
                <input name="first_name" type="text" value={form.first_name} onChange={handleChange} placeholder="Jane" style={inputStyle} onFocus={focusStyle} onBlur={blurStyle} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Last Name *</label>
                <input name="last_name" type="text" value={form.last_name} onChange={handleChange} placeholder="Smith" style={inputStyle} onFocus={focusStyle} onBlur={blurStyle} />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address *</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@example.com" autoComplete="email" style={inputStyle} onFocus={focusStyle} onBlur={blurStyle} />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password *</label>
              <input name="password" type="password" value={form.password} onChange={handleChange} placeholder="Create a password (min. 8 characters)" autoComplete="new-password" style={inputStyle} onFocus={focusStyle} onBlur={blurStyle} />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password *</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                placeholder="Re-enter your password"
                autoComplete="new-password"
                style={inputStyle}
                onFocus={focusStyle}
                onBlur={blurStyle}
              />
              {confirmPassword && form.password && (
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: form.password === confirmPassword ? '#166534' : '#ff082f' }}>
                  {form.password === confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                </p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number <span style={{ color: '#ff082f' }}>*</span></label>
              <input name="phone" type="tel" inputMode="numeric" value={form.phone} maxLength={14}
                onChange={e => { setForm(p => ({ ...p, phone: formatPhone(e.target.value) })); setError(''); }}
                placeholder="(555) 000-0000"
                style={{ ...inputStyle, borderColor: form.phone && !isValidPhone(form.phone) ? '#ff082f' : '#e5e7eb' }}
                onFocus={focusStyle} onBlur={blurStyle} />
              {form.phone && !isValidPhone(form.phone) && (
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#ff082f' }}>Enter a valid 10-digit phone number</p>
              )}
            </div>

            {/* 🚨 NO LICENSE FIELD AT ALL FOR A TYPE THAT HOLDS NO LICENSE.
                Rendering it disabled, or with a "not applicable" note, still puts
                a license question in front of a homebuilder and invites them to
                type something into it. `start && !ask` means the questions were
                answered AND the pair carries none — an absent `start` keeps the
                field, because not knowing is not the same as knowing there is
                none. */}
            {(!start || ask) && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                {/* ⚠️ THE LABEL NAMES WHICH LICENSE, AND IT COMES FROM THE SERVER.
                    This used to read "NMLS Number" or "License Number" off
                    partner_type alone — two labels for four different licenses.
                    A realtor agent holds a Sales Agent number and a realtor
                    broker a Broker number; an LO holds an individual NMLS id and
                    a mortgage broker a company one. One label for each pair, or
                    a correct number gets filed as the wrong kind and nothing
                    records which it was. */}
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {ask ? ask.label : 'License Number'} <span style={{ color: '#ff082f' }}>*</span>
                </label>
                <input name="license_number" type="text" value={form.license_number} onChange={handleChange}
                  placeholder={ask && ask.registry === 'nmls' ? '1234567' : 'TX-12345678'}
                  style={inputStyle} onFocus={focusStyle} onBlur={blurStyle} />
                {ask && ask.help && (
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280' }}>{ask.help}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">License State <span style={{ color: '#ff082f' }}>*</span></label>
                {/* ⚠️ A SELECT, NOT A TEXT BOX. This uppercased what was typed,
                    which was enough here — but the profile editor did not, and
                    between them the column ended up holding 'TX' and 'Tx' as two
                    different states. A control that accepts typing gets typed
                    into. The server normalises regardless; see
                    backend/utils/normalizeState.js. */}
                {/* ⚠️ PICKING A LICENSE STATE PRE-FILLS THE WORK STATE, and only
                    while the work state is still untouched. For a realtor the two
                    genuinely coincide — a state real-estate license is bound to
                    its state — so asking twice would be asking the same question
                    twice. For everyone else it is a starting point they can
                    change. */}
                <select name="license_state" value={form.license_state}
                  onChange={e => {
                    const v = e.target.value;
                    setForm(p => ({
                      ...p, license_state: v, work_state: p.work_state || v,
                      // 🔑 THE PRIMARY IS ALWAYS IN THE SET. Changing it swaps the
                      // old primary out rather than accumulating every state the
                      // user browsed past.
                      license_states: [v, ...p.license_states.filter((x) => x !== v && x !== p.license_state)].filter(Boolean),
                    }));
                    setError('');
                  }}
                  style={{ ...inputStyle }} onFocus={focusStyle} onBlur={blurStyle}>
                  <option value="">Select a state…</option>
                  {US_STATES.map(([abbr, name]) => <option key={abbr} value={abbr}>{name}</option>)}
                </select>

                {/* ── 🔑 LICENSED IN MORE THAN ONE STATE ──────────────────
                    🚨 THE COLUMN WAS SINGULAR AND THAT IS WRONG FOR THE PEOPLE
                    MOST LIKELY TO MATTER. A loan officer is routinely licensed in
                    several states — NMLS is one national identifier with a
                    per-state authorisation behind it — and a realtor near a
                    border often holds two. The form made them pick one and the
                    rest were lost.

                    ⚠️ AN ADD-ONE-AT-A-TIME LIST, NOT A MULTI-SELECT. A native
                    multiple <select> needs ctrl/cmd-click to add a second value
                    and silently REPLACES the selection otherwise — the control
                    most likely to lose the answer it exists to collect.

                    ⚠️ AND THE PRIMARY CANNOT BE REMOVED HERE. It is the one the
                    license number above belongs to, so it is shown as a fixed
                    chip and changed by changing that dropdown. */}
                {form.license_state && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                      {form.license_states.map((st) => (
                        <span key={st} style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                          background: st === form.license_state ? '#eef2ff' : '#f3f4f6',
                          border: '1px solid #e5e7eb', borderRadius: 999, padding: '3px 10px', fontSize: 12.5, color: '#374151' }}>
                          {st}{st === form.license_state && <em style={{ fontStyle: 'normal', color: '#6b7280', fontSize: 11 }}>primary</em>}
                          {st !== form.license_state && (
                            <button type="button" aria-label={`Remove ${st}`}
                              onClick={() => setForm(p => ({ ...p, license_states: p.license_states.filter((x) => x !== st) }))}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
                          )}
                        </span>
                      ))}
                    </div>
                    <select value="" style={{ ...inputStyle, fontSize: 13 }}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (!v) return;
                        setForm(p => ({ ...p, license_states: p.license_states.includes(v) ? p.license_states : [...p.license_states, v] }));
                      }}>
                      <option value="">+ Add another state you&rsquo;re licensed in…</option>
                      {US_STATES.filter(([abbr]) => !form.license_states.includes(abbr))
                        .map(([abbr, name]) => <option key={abbr} value={abbr}>{name}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* ── 🚨 THE SECOND NUMBER A MORTGAGE BROKER HOLDS ─────────────
                The help text under the first field promised "we ask for that
                next" and nothing did — so the one number collected went into
                the individual field whatever it was, and every mortgage broker
                on the books has their COMPANY number there. The ask declares
                this field; the server routes it to partner_accounts. */}
            {ask && ask.alsoAsk && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {ask.alsoAsk.label} <span style={{ color: '#ff082f' }}>*</span>
                </label>
                <input name={ask.alsoAsk.field} type="text"
                  value={form[ask.alsoAsk.field] || ''} onChange={handleChange}
                  placeholder="1234567"
                  style={inputStyle} onFocus={focusStyle} onBlur={blurStyle} />
                {ask.alsoAsk.help && (
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280' }}>
                    {ask.alsoAsk.help}</p>
                )}
              </div>
            )}

            {/* ── 🔑 WHERE DO YOU WORK ──────────────────────────────────────
                🚨 ASKED DIRECTLY BECAUSE NOTHING ELSE COULD ANSWER IT. This
                decides which Partner Success rep the partner gets, and the
                license state could not stand in for it:
                  · a loan officer's NMLS id is NATIONAL, and the field beside it
                    says "License State" — every live LO carries 'TX', including
                    one with a Maryland phone;
                  · a homebuilder is asked for no license at all, so they had no
                    state on the record whatsoever.
                The alternatives were worse: phone area codes were wrong for three
                of the seven partners where a TREC county could check them, and
                TREC county exists for no loan officer and fewer than half the
                realtors.

                ⚠️ ASKED OF EVERYONE, OUTSIDE THE LICENSE BLOCK. Inside it, a
                homebuilder — the type with no other state at all — would be the
                one person never asked. */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Where do you primarily work? <span style={{ color: '#ff082f' }}>*</span>
              </label>
              <select name="work_state" value={form.work_state}
                onChange={e => { setForm(p => ({ ...p, work_state: e.target.value })); setError(''); }}
                style={{ ...inputStyle }} onFocus={focusStyle} onBlur={blurStyle}>
                <option value="">Select a state…</option>
                {US_STATES.map(([abbr, name]) => <option key={abbr} value={abbr}>{name}</option>)}
              </select>
              {/* ⚠️ SAYS WHY IT IS ASKED AND WHAT IT IS NOT. Without this it reads
                  as a duplicate of the license state, and the honest answer —
                  "so we can put the right person on your account" — is also the
                  one that gets a truthful answer. */}
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280' }}>
                The state you mostly do business in — we use it to put the right Doorly contact on
                your account.
              </p>

              {/* ── 🔑 WHERE THEY ARE, SAID BACK TO THEM ───────────────────
                  🚨 NOTHING TOLD AN OUT-OF-STATE PARTNER ANYTHING. Registration
                  has no state gate and no state-varying copy anywhere in the
                  portal — a realtor in Nashville saw the identical form a Texan
                  sees, onboarded, signed an agreement, and found out only when a
                  buyer was refused. PROGRAM.states gates the BORROWER side and
                  nothing on the partner side read it.

                  🔑 AND IT IS AN INVITATION, NOT A GATE. Doorly opens states
                  where there is demand, so a sign-up from a closed state is
                  evidence FOR opening it — refusing at the door throws away both
                  the relationship and the signal. The copy lives on the server
                  (constants/partnerFootprint.js) because the footprint moves when
                  a license lands, and a copy in the bundle would go on telling
                  people a state is closed after it opened. */}
              {footprint && (
                <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 10, lineHeight: 1.6,
                  background: footprint.status === 'pending' ? '#f0fdf4' : '#f5f8ff',
                  border: `1px solid ${footprint.status === 'pending' ? '#bbf7d0' : '#c5d3f5'}`,
                  fontSize: 13, color: '#374151' }}>
                  <strong style={{ display: 'block', marginBottom: 3, color: '#111827' }}>{footprint.headline}</strong>
                  {footprint.body}
                </div>
              )}
            </div>

            {/* ⚠️ SPACING, NOT DECORATION. The work-state block and Company Name
                were flush against each other, so the help text under one read as
                a caption for the other. */}
            <div style={{ height: 8 }} />

            {/* 🚨 THE PARTNER TYPE DROPDOWN IS GONE — ASKED AT /doorlypro/start.
                Leaving it here would let a partner answer the same question
                twice and differently, and the second answer would silently win.
                It is not a lost gate: the <select> was self-asserted too. */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Company Name <span style={{ color: '#ff082f' }}>*</span>
              </label>
              <input name="company_name" type="text" value={form.company_name} onChange={handleChange}
                placeholder="The firm you work under"
                style={inputStyle} onFocus={focusStyle} onBlur={blurStyle} />
            </div>

            {/* ⚠️ THE CONSEQUENCE IS STATED WHERE IT IS CHOSEN. Registering as the
                firm creates the firm's account and makes this person its broker —
                they counter-sign their team's agreements and see the team's
                pipeline. Nothing on a name-and-email form implies that, and the
                one screen back is the cheapest place to correct it.

                🔑 MOVED BELOW COMPANY NAME. It used to sit above the license
                block, which is the first thing on the form — so the reader met a
                paragraph about counter-signing before they had typed a name. It
                now follows the field it is actually about: having named the firm,
                they are told what naming it makes them. */}
            {(isAccountRegistration || (start && start.enterprise)) && (
              <div style={{ marginBottom: 16, padding: '12px 14px', background: '#f5f8ff', border: '1px solid #c5d3f5', borderRadius: 10, fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                You're setting up <strong>{accountNotice.firm}</strong>. That makes you {accountNotice.role} here:
                you'll counter-sign your team's agreements and see their buyers alongside your own.
                Your {accountNotice.people} each sign their own agreement.
                {' '}
                <Link to="/doorlypro/start" style={{ color: '#3a6ad9', fontWeight: 600 }}>Not you?</Link>
              </div>
            )}


            {/* 🔑 ASKED ONLY ON THE INDIVIDUAL PATHS, AND FOR AN ORIGINATOR IT IS
                THE ONLY RELIABLE SIGNAL THERE IS. A realtor's firm can be checked
                against the state license record; a mortgage company cannot be
                checked against anything, because NMLS Consumer Access forbids the
                bulk use a lookup would need. So this is not a convenience
                question — it is the whole of the available evidence.

                ⚠️ NOT ASKED OF A BROKER OR TEAM LEAD: they are registering the
                firm, so "does your broker have an account" is a question about
                themselves.

                ⚠️ "Not sure" IS A REAL OPTION AND IS STORED AS ITSELF. Forcing a
                yes/no makes people guess, and a guess recorded as fact is worse
                than a recorded "we were not told". */}
            {start && !start.enterprise && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Does your {start.partner_type === 'loan_officer' ? 'company' : 'broker'} already have a DoorlyPro account?
                </label>
                <select name="broker_account_claim" value={form.broker_account_claim} onChange={handleChange}
                  style={{ ...inputStyle, cursor: 'pointer' }} onFocus={focusStyle} onBlur={blurStyle}>
                  <option value="">Select…</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="unsure">I’m not sure</option>
                </select>
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280' }}>
                  If they do, we’ll add you to their account instead of creating a second one.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary btn-full text-white hover:opacity-90 disabled:opacity-60"
              style={{ backgroundColor: '#3a6ad9' }}
            >
              {loading ? 'Creating Account...' : (
                <>
                  <span>Create Partner Account</span>
                  <span className="btn-arrow">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg>
                  </span>
                </>
              )}
            </button>
          </form>
          </>
          )}
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          Already have an account?{' '}
          <Link to="/doorlypro/login" className="font-semibold" style={{ color: '#3a6ad9' }}>
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
